/* eslint-disable no-underscore-dangle */
import { useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { ColDef, ColGroupDef, EditableCallbackParams } from 'ag-grid-enterprise'
import { Dayjs } from 'dayjs'
import { FinancialStatementRowAction, StatementColumn, StatementDataType } from '../types'
import { EnhancedStatementRow } from '../utils/dataEnhancement'
import { DualValueCellRenderer } from '../components/StatementTable/cellRenderers/DualValueCellRenderer'
import ValueCellRenderer from '../components/StatementTable/cellRenderers/ValueCellRenderer'
import DualValueCellEditor from '../components/StatementTable/cellEditors/DualValueCellEditor'
import NumericCellEditor from '../../../shared/components/custom/Grid/Editor/NumericCellEditor'
import PercentageCellEditor from '../../../shared/components/custom/Grid/Editor/PercentageCellEditor'
import { ReportDataType } from '../../../redux/context/reports/types'
import { createMonthOnlyColumnGroupDefs } from '../utils/columnGrouping'
import {
  createAutoGroupColumnDef,
  createHiddenExportColumns,
  getCommonColumnProps,
  getBaseCellClasses,
  shouldUseDualValueDisplay,
  isRowEditable
} from '../utils/columnUtils'
import {
  extractDataValue,
  setRegistryValueWithDirtyTracking,
  isCellModified,
  getChangesRegistryKey
} from '../../../shared/utils/balanceRegistry'
import { useAgGridLocale } from '../../../shared/hooks/useAgGridLocale'
import { GridContext } from '../../../shared/hooks/useGridContext'
import { useCtxCommentsMap } from '../../budgetComments/queries/useCtxCommentsMap'
import { contextCompanySelector } from '../../../redux/context/company/selectors'
import { getCellKey } from '../../budgetComments/utils/commentUtils'
import { filtersSelector } from '../../../redux/context/filters/selectors'

/**
 * Common params interface for cell callbacks
 */
interface CellParams {
  data?: EnhancedStatementRow | null
  context?: GridContext
}

interface UseIncomeStatementColumnsOptions {
  columns: StatementColumn[]
  onRowAction?: (rowData: EnhancedStatementRow, action: FinancialStatementRowAction) => void
  dataTypes?: ReportDataType[]
}

interface UseIncomeStatementColumnsReturn {
  columnDefs: (StatementColDef | ColGroupDef<EnhancedStatementRow>)[]
  autoGroupColumnDef: ColDef<EnhancedStatementRow>
}

export interface StatementColumnContext {
  column?: StatementColumn
  showInput?: boolean
}

export interface StatementColDef extends ColDef<EnhancedStatementRow, any> {
  context?: StatementColumnContext
}

/**
 * Helper function to check if a forecast column is after the forecastCursor
 * Forecast columns after the cursor are editable, while those before/at cursor use actual data
 */
const isForecastColumnAfterCursor = (column: StatementColumn, forecastCursor?: Dayjs): boolean => {
  if (!forecastCursor || column.year === undefined || column.month === undefined) {
    return false
  }

  return (
    column.year > forecastCursor.year() ||
    (column.year === forecastCursor.year() && column.month > forecastCursor.month() + 1)
  )
}

export const useIncomeStatementColumns = ({
  columns,
  onRowAction,
  dataTypes
}: UseIncomeStatementColumnsOptions): UseIncomeStatementColumnsReturn => {
  const localeText = useAgGridLocale()
  const { data: commentMap } = useCtxCommentsMap()
  const accountingSoftware = useSelector(contextCompanySelector)?.accountingSoftware
  const { monthSortOrder } = useSelector(filtersSelector)

  // Auto group column definition for tree data
  const autoGroupColumnDef = useMemo(
    (): StatementColDef =>
      createAutoGroupColumnDef({
        localeText,
        onRowAction
      }),
    [localeText, onRowAction]
  )

  // Helper function to create a column definition for a single column
  const createColumnDef = useCallback(
    (column: StatementColumn): StatementColDef => {
      const isCellEditable = (params: CellParams): boolean => {
        if (!params.context?.showInput) return false
        if (!params.data) return false
        if (column.dataType === StatementDataType.actuals) return false

        if (column.dataType === StatementDataType.forecast) {
          // Forecast columns are only editable if they're after the forecastCursor
          if (
            column.dataSource === 'elements' &&
            !isForecastColumnAfterCursor(column, params.context?.forecastCursor)
          ) {
            return false
          }
        }

        return isRowEditable(params.data)
      }

      return {
        ...getCommonColumnProps(column),
        context: {
          column
        },
        valueGetter: params => {
          const { dataType, dataKey, dataSource } = column
          if (!params.data || !dataType || dataKey === undefined) return null

          if (dataType === 'budget' || dataType === 'forecast') {
            if (isCellEditable(params)) {
              const editedValue = extractDataValue(params.data, column, 'newBudgets')
              if (editedValue !== undefined) {
                return editedValue // Can be null (deleted)
              }
              return extractDataValue(params.data, column, 'budgets')
            }
          }

          return extractDataValue(params.data, column)
        },
        editable: params => isCellEditable(params) && params.context.isEditMode,
        cellRendererParams: (params: CellParams) => {
          const commentKey = getCellKey(params.data, column)
          return {
            hasComments: commentKey ? commentMap?.has(commentKey) : false
          }
        },
        cellRendererSelector: params => {
          const showInput = params.context?.showInput
          const isEdited = showInput && isCellModified(params.data, column, getChangesRegistryKey(column.dataType))

          // Use dual value display only for directly editable rows (excludes driver-based)
          if (shouldUseDualValueDisplay(params) && column.dataType !== 'actuals') {
            return {
              component: DualValueCellRenderer
            }
          }

          // Otherwise use ValueCellRenderer (single value or budgeting not active)
          return {
            component: ValueCellRenderer,
            params: {
              isEdited
            }
          }
        },
        cellEditorSelector: params => {
          // Check if budgeting method is 'reference' - use percentage editor
          const isReferenceMethod = params.data?.budgetingMethod?.method === 'reference'

          const showCalculated = params.context?.showCalculated
          const showInput = params.context?.showInput

          // If showing both calculated and input, use DualValueCellEditor
          if (showCalculated && showInput) {
            return {
              component: DualValueCellEditor,
              params: {
                allowNegative: true,
                allowDecimals: true,
                placeholder: ''
              }
            }
          }

          // If reference method, use PercentageCellEditor
          if (isReferenceMethod) {
            return {
              component: PercentageCellEditor,
              params: {
                allowNegative: true,
                placeholder: ''
              }
            }
          }

          // Otherwise use NumericCellEditor (for single value editing)
          // NumericCellEditor will receive the value from the grid's valueGetter
          // which already handles priority: newBudgets > budgets > budget
          return {
            component: NumericCellEditor,
            params: {
              allowNegative: true,
              allowDecimals: true,
              placeholder: ''
            }
          }
        },
        valueSetter: params => {
          if (!params.data) return false

          const { dataKey } = column
          if (dataKey === undefined) return false

          // Use shared dirty tracking function
          // 'budgets' = saved user input (original value)
          // 'newBudgets' = unsaved changes
          return setRegistryValueWithDirtyTracking(params.data, column, params.newValue, 'budgets', 'newBudgets')
        },
        cellClass: params => {
          const classes = getBaseCellClasses(params, column, accountingSoftware)

          // Add editable indicator class
          if (isCellEditable(params)) {
            classes.push('cell-editable')
          }

          // Add cell-edited class when showing input values
          const showInput = params.context?.showInput
          if (showInput && isCellModified(params.data, column, 'newBudgets')) {
            classes.push('cell-edited')
          }

          return classes
        }
      }
    },
    [dataTypes, commentMap, accountingSoftware]
  )

  // Memoized column definitions
  const columnDefs = useMemo((): (ColDef<EnhancedStatementRow> | ColGroupDef<EnhancedStatementRow>)[] => {
    const cols: (ColDef<EnhancedStatementRow> | ColGroupDef<EnhancedStatementRow>)[] = []

    // Add hidden export columns (account code and dimension)
    cols.push(...createHiddenExportColumns())

    // cols.push({
    //   field: 'dimensionId'
    // })

    // Use month grouping for income statement
    const groupedCols = createMonthOnlyColumnGroupDefs(
      columns,
      createColumnDef,
      false, // Use full month names
      true, // Include cumulative columns
      dataTypes,
      monthSortOrder
    )
    cols.push(...groupedCols)

    return cols
  }, [columns, createColumnDef, dataTypes, monthSortOrder])

  return {
    columnDefs,
    autoGroupColumnDef
  }
}
